import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  UserPlus, ExternalLink, RefreshCw, Search,
  Mail, Phone, Users, CheckCircle2, MapPin, Trash2,
  Sparkles, Loader2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ENRICH_MESSAGES = [
  "🕵️ Snooping through LinkedIn like a pro...",
  "🔮 Consulting the crystal ball for contact data...",
  "🐕 Sniffing out email addresses like a bloodhound...",
  "🎣 Fishing for phone numbers in the data ocean...",
  "🔬 Running forensic analysis on digital footprints...",
  "🧙 Casting a contact-finding spell...",
  "🚀 Launching the enrichment rockets...",
  "🎯 Zeroing in on decision maker intel...",
  "🦊 Being sly and resourceful finding contacts...",
  "☕ Grabbing a coffee while the data flows in...",
  "🧲 Magnetically attracting contact details...",
  "🌊 Surfing the web for hidden email addresses...",
  "🏴‍☠️ Plundering the seven seas of data...",
  "🤖 Deploying enrichment bots... beep boop...",
  "🎰 Spinning the enrichment wheel of fortune...",
  "🪄 Abracadabra... give me that email!",
  "🐝 Buzzing through databases like a busy bee...",
  "🕸️ Weaving through the web of connections...",
  "🧃 Squeezing out every last drop of data...",
  "💎 Mining for contact diamonds in the rough...",
];

const SENIORITY_ORDER = ['c_level', 'vp', 'director', 'manager'];

function SeniorityBadge({ seniority }) {
  const colors = {
    c_level: 'bg-purple-100 text-purple-800',
    vp: 'bg-blue-100 text-blue-800',
    director: 'bg-indigo-100 text-indigo-800',
    manager: 'bg-gray-100 text-gray-600',
  };
  const color = colors[seniority] || colors.manager;
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${color}`}>
      {(seniority || 'unknown').replace('_', '-').toUpperCase()}
    </span>
  );
}

function EmailConfidenceBadge({ confidence }) {
  if (!confidence) return null;
  const colors = {
    verified: 'bg-green-100 text-green-700',
    likely: 'bg-yellow-100 text-yellow-700',
    guessed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`text-xs px-1 py-0.5 rounded ${colors[confidence] || 'bg-gray-100 text-gray-500'}`}>
      {confidence}
    </span>
  );
}

export function CompanyDecisionMakersTab({ companyId, companyName }) {
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [addingId, setAddingId]   = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [filter, setFilter]       = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [enrichingId, setEnrichingId] = useState(null);
  const [enrichingAll, setEnrichingAll] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState({ current: 0, total: 0, currentName: '', message: '', results: [] });
  const enrichCancelRef = useRef(false);

  const fetchResearch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/companies/${companyId}/research`, { withCredentials: true });
      setResearch(res.data);
    } catch (e) {
      console.error('Failed to load decision makers', e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchResearch(); }, [fetchResearch]);

  const discoverDecisionMakers = async () => {
    setDiscovering(true);
    try {
      await axios.post(`${API}/api/companies/${companyId}/research`, {}, { withCredentials: true });
      toast.success('Research started — discovering decision makers...');
      // Poll for results
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/api/companies/${companyId}/research`, { withCredentials: true });
          if (res.data.status !== 'in_progress') {
            clearInterval(poll);
            setResearch(res.data);
            setDiscovering(false);
            const count = (res.data.decision_makers || []).length;
            if (count > 0) {
              toast.success(`Found ${count} decision maker${count === 1 ? '' : 's'}`);
            } else {
              toast.info('Research complete — no decision makers found');
            }
          }
        } catch {
          clearInterval(poll);
          setDiscovering(false);
        }
      }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start research');
      setDiscovering(false);
    }
  };

  const removeDM = async (dm) => {
    setRemovingId(dm.id);
    try {
      await axios.delete(
        `${API}/api/companies/${companyId}/research/decision-makers/${dm.id}`,
        { withCredentials: true }
      );
      toast.success(`${dm.name} removed`);
      fetchResearch();
    } catch {
      toast.error('Failed to remove');
    } finally {
      setRemovingId(null);
    }
  };

  const addAsLead = async (dm) => {
    setAddingId(dm.id);
    try {
      await axios.post(
        `${API}/api/companies/${companyId}/research/decision-makers/${dm.id}/add-as-lead`,
        {},
        { withCredentials: true }
      );
      toast.success(`${dm.name} added as lead`);
      fetchResearch();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to add lead';
      if (msg.includes('already exists')) {
        toast.info('This person is already in your CRM');
      } else {
        toast.error(msg);
      }
    } finally {
      setAddingId(null);
    }
  };

  const enrichDM = async (dm) => {
    setEnrichingId(dm.id);
    try {
      const res = await axios.post(
        `${API}/api/companies/${companyId}/research/decision-makers/${dm.id}/enrich`,
        {},
        { withCredentials: true }
      );
      if (res.data.status === 'enriched') {
        const u = res.data.updates;
        const parts = [];
        if (u.email) parts.push('email');
        if (u.phone) parts.push('phone');
        const src = u.enrich_source === 'apollo' ? ' (via Apollo)' : ' (via Pulse)';
        toast.success(`Found ${parts.join(' & ')} for ${dm.name}${src}`);
      } else {
        toast.info(`No contact data found for ${dm.name}`);
      }
      fetchResearch();
    } catch {
      toast.error('Failed to enrich contact');
    } finally {
      setEnrichingId(null);
    }
  };

  const [enrichStats, setEnrichStats] = useState(null);

  const enrichAll = async () => {
    const dms = research?.decision_makers || [];
    const toEnrich = dms.filter(dm => !dm.email || !dm.phone);
    if (toEnrich.length === 0) {
      toast.info('All decision makers already have contact data');
      return;
    }

    setEnrichingAll(true);
    setEnrichStats(null);
    enrichCancelRef.current = false;
    const results = [];
    let pulseCount = 0;
    let apolloCount = 0;
    let noDataCount = 0;
    setEnrichProgress({ current: 0, total: toEnrich.length, currentName: '', message: ENRICH_MESSAGES[0], results: [] });

    for (let i = 0; i < toEnrich.length; i++) {
      if (enrichCancelRef.current) {
        toast.info(`🛑 Enrichment cancelled after ${i} of ${toEnrich.length}`);
        break;
      }

      const dm = toEnrich[i];
      const msg = ENRICH_MESSAGES[i % ENRICH_MESSAGES.length];
      setEnrichProgress({ current: i + 1, total: toEnrich.length, currentName: dm.name, message: msg, results: [...results] });

      try {
        const res = await axios.post(
          `${API}/api/companies/${companyId}/research/decision-makers/${dm.id}/enrich`,
          {},
          { withCredentials: true }
        );
        const found = [];
        let source = '';
        if (res.data.status === 'enriched') {
          if (res.data.updates?.email) found.push('📧');
          if (res.data.updates?.phone) found.push('📱');
          source = res.data.updates?.enrich_source || 'pulse';
          if (source === 'apollo') apolloCount++;
          else pulseCount++;
        } else {
          noDataCount++;
        }
        const sourceIcon = source === 'apollo' ? '🅰️' : source === 'pulse' ? '🔵' : '';
        results.push({ name: dm.name, found: found.length > 0 ? `${found.join(' ')} ${sourceIcon}` : '❌' });
      } catch {
        noDataCount++;
        results.push({ name: dm.name, found: '⚠️' });
      }
    }

    const stats = { pulse: pulseCount, apollo: apolloCount, noData: noDataCount, total: results.length };
    setEnrichStats(stats);
    setEnrichProgress(prev => ({ ...prev, results, currentName: '' }));
    await fetchResearch();
    setEnrichingAll(false);

    if (!enrichCancelRef.current) {
      const parts = [];
      if (pulseCount > 0) parts.push(`${pulseCount} from Pulse`);
      if (apolloCount > 0) parts.push(`${apolloCount} from Apollo`);
      const found = pulseCount + apolloCount;
      toast.success(`🎉 Done! Enriched ${found} of ${stats.total} — ${parts.join(', ')}${noDataCount > 0 ? ` (${noDataCount} no data)` : ''}`);
    }
  };

  const cancelEnrichAll = () => {
    enrichCancelRef.current = true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        Loading decision makers...
      </div>
    );
  }

  const dms = research?.decision_makers || [];

  if (!research || research.status === 'not_researched' || dms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
        <Users className="w-14 h-14 text-gray-200" />
        <h3 className="text-lg font-semibold text-gray-700">No Decision Makers Found</h3>
        <p className="text-sm text-gray-400 max-w-sm">
          Discover key decision makers and their contact info at {companyName || 'this company'}.
        </p>
        <Button
          onClick={discoverDecisionMakers}
          disabled={discovering}
          className="mt-2 gap-2"
        >
          {discovering ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Discovering...
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              Find Decision Makers
            </>
          )}
        </Button>
      </div>
    );
  }

  const sorted = [...dms].sort((a, b) => {
    // CRM leads always come first
    if (a.is_crm_lead && !b.is_crm_lead) return -1;
    if (!a.is_crm_lead && b.is_crm_lead) return 1;
    // Then by seniority
    const ai = SENIORITY_ORDER.indexOf(a.seniority || 'manager');
    const bi = SENIORITY_ORDER.indexOf(b.seniority || 'manager');
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const filtered = filter
    ? sorted.filter(d =>
        d.name?.toLowerCase().includes(filter.toLowerCase()) ||
        d.title?.toLowerCase().includes(filter.toLowerCase())
      )
    : sorted;

  const inCrmCount = dms.filter(d => d.is_crm_lead).length;

  return (
    <div className="pb-8">
      {/* Summary bar */}
      <div className="flex items-center justify-between mb-4 px-1 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            {dms.length} decision makers identified
            {inCrmCount > 0 && (
              <span className="text-green-600 ml-1">· {inCrmCount} already in CRM</span>
            )}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs h-7 px-2.5 text-purple-700 border-purple-200 hover:bg-purple-50"
            disabled={enrichingAll}
            onClick={enrichAll}
          >
            {enrichingAll ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {enrichingAll ? 'Enriching...' : 'Enrich All'}
          </Button>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2 text-gray-400" />
          <input
            type="text"
            placeholder="Filter by name or title..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 w-52"
          />
        </div>
      </div>

      {/* Enrich All Progress Banner */}
      {enrichingAll && (
        <div className="mb-4 rounded-xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 p-4 animate-in fade-in">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />
              </div>
              <span className="text-sm font-bold text-purple-800">
                Enriching {enrichProgress.current} of {enrichProgress.total}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs h-7 px-3 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={cancelEnrichAll}
            >
              <XCircle className="w-3.5 h-3.5" />
              Cancel
            </Button>
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 bg-purple-100 rounded-full overflow-hidden mb-3">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${(enrichProgress.current / enrichProgress.total) * 100}%` }}
            />
          </div>

          {/* Current person being enriched */}
          {enrichProgress.currentName && (
            <div className="flex items-center gap-2 mb-2">
              <Loader2 className="w-4 h-4 animate-spin text-purple-500 shrink-0" />
              <span className="text-sm font-semibold text-gray-700 truncate">
                {enrichProgress.currentName}
              </span>
            </div>
          )}

          {/* Funny message */}
          <p className="text-xs text-purple-600 font-medium italic">
            {enrichProgress.message}
          </p>

          {/* Results so far */}
          {enrichProgress.results.length > 0 && (
            <div className="mt-3 pt-3 border-t border-purple-100">
              <div className="flex flex-wrap gap-1.5">
                {enrichProgress.results.map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-white border border-purple-100 rounded-full px-2 py-0.5">
                    <span className="font-medium text-gray-700 truncate max-w-[120px]">{r.name.split(' ')[0]}</span>
                    <span>{r.found}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Enrich Stats Summary */}
      {enrichStats && !enrichingAll && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-green-800">Enrichment Complete</span>
            <button onClick={() => setEnrichStats(null)} className="text-gray-400 hover:text-gray-600 text-xs">dismiss</button>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0" />
              <span className="font-semibold text-gray-700">{enrichStats.pulse}</span>
              <span className="text-gray-500">from Pulse</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-orange-500 shrink-0" />
              <span className="font-semibold text-gray-700">{enrichStats.apollo}</span>
              <span className="text-gray-500">from Apollo</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-300 shrink-0" />
              <span className="font-semibold text-gray-700">{enrichStats.noData}</span>
              <span className="text-gray-500">no data</span>
            </div>
          </div>
        </div>
      )}

      {filtered.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-8">No results match "{filter}"</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map(dm => (
          <div
            key={dm.id}
            onMouseEnter={() => setHoveredId(dm.id)}
            onMouseLeave={() => setHoveredId(null)}
            className={`border rounded-lg p-4 transition-shadow hover:shadow-sm ${
              dm.is_crm_lead ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-gray-900 text-sm">{dm.name}</h4>
                  {dm.seniority_inferred && <SeniorityBadge seniority={dm.seniority_inferred} />}
                  {!dm.seniority_inferred && dm.seniority && <SeniorityBadge seniority={dm.seniority} />}
                  {dm.rank && (
                    <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-indigo-50 text-indigo-700">#{dm.rank}</span>
                  )}
                  {dm.influence_score != null && (
                    <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-slate-100 text-slate-600">Score: {dm.influence_score}</span>
                  )}
                  {dm.is_crm_lead && (
                    <span className="text-xs flex items-center gap-0.5 text-green-700 font-medium">
                      <CheckCircle2 className="w-3 h-3" />In CRM
                    </span>
                  )}
                  {dm.is_new_hire && (
                    <Badge className="bg-orange-100 text-orange-800 text-xs border-0">New Hire</Badge>
                  )}
                </div>
                {dm.title && <p className="text-xs text-gray-500 mt-0.5 truncate">{dm.title}</p>}
                {dm.department && <p className="text-xs text-gray-400">{dm.department}</p>}
              </div>

              <div className={`flex items-center gap-1 shrink-0 transition-opacity duration-150 ${hoveredId === dm.id || addingId === dm.id || removingId === dm.id || enrichingId === dm.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                {(!dm.email || !dm.phone) && (
                  <button
                    className="p-1 rounded hover:bg-purple-50 text-gray-300 hover:text-purple-600 transition-colors"
                    title="Enrich contact data"
                    disabled={enrichingId === dm.id}
                    onClick={() => enrichDM(dm)}
                  >
                    {enrichingId === dm.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-500" />
                      : <Sparkles className="w-3.5 h-3.5" />}
                  </button>
                )}
                <Button
                  size="sm"
                  variant={dm.is_crm_lead ? "ghost" : "outline"}
                  className="gap-1 text-xs h-7 px-2"
                  disabled={dm.is_crm_lead || addingId === dm.id}
                  onClick={() => !dm.is_crm_lead && addAsLead(dm)}
                >
                  {addingId === dm.id ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <UserPlus className="w-3 h-3" />
                  )}
                  {dm.is_crm_lead ? 'In CRM' : 'Add Lead'}
                </Button>
                <button
                  className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                  title="Remove from list"
                  disabled={removingId === dm.id}
                  onClick={() => removeDM(dm)}
                >
                  {removingId === dm.id
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Contact info */}
            <div className="mt-2.5 space-y-1">
              {dm.email && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <a href={`mailto:${dm.email}`} className="hover:underline truncate">{dm.email}</a>
                  <EmailConfidenceBadge confidence={dm.email_confidence} />
                </div>
              )}
              {dm.phone && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <a href={`tel:${dm.phone}`} className="hover:underline">{dm.phone}</a>
                </div>
              )}
              {(dm.linkedin_url || dm.location) && (
                <div className="flex items-center gap-3 flex-wrap">
                  {dm.linkedin_url && (
                    <div className="flex items-center gap-1.5 text-xs">
                      <svg className="w-3.5 h-3.5 text-blue-400 shrink-0 fill-current" viewBox="0 0 24 24">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/>
                        <circle cx="4" cy="4" r="2"/>
                      </svg>
                      <a href={dm.linkedin_url} target="_blank" rel="noopener noreferrer"
                         className="text-blue-600 flex items-center gap-0.5 hover:underline">
                        LinkedIn <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  )}
                  {dm.location && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                      <span>{dm.location}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {dm.bio_summary && (
              <p className="text-xs text-gray-400 mt-2 leading-relaxed line-clamp-2">{dm.bio_summary}</p>
            )}

            {dm.tenure_months > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                Tenure: {Math.floor(dm.tenure_months / 12) > 0
                  ? `${Math.floor(dm.tenure_months / 12)}y ${dm.tenure_months % 12}m`
                  : `${dm.tenure_months}m`}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
